import BN from 'bn.js';
import { BinaryWriter } from './BinaryWriter';
import { common } from './common';
import { ByteBuffer, Op } from './models/vm';
import { scriptBuilderParamTo } from './paramUtils';
import { utils } from './utils';
export class ScriptBuilder {
    constructor() {
        this.mutableBuffers = [];
        this.pushParamCallbacks = {
            undefined: () => this.emitPush(Buffer.alloc(0, 0)),
            array: (param) => this.emitPushArray(param),
            map: (param) => this.emitPushMap(param),
            uInt160: (param) => this.emitPushUInt160(common.asUInt160(param)),
            uInt256: (param) => this.emitPushUInt256(common.asUInt256(param)),
            ecPoint: (param) => this.emitPushECPoint(common.asECPoint(param)),
            number: (param) => this.emitPushInt(param),
            bn: (param) => this.emitPushInt(param),
            string: (param) => this.emitPushString(param),
            boolean: (param) => this.emitPushBoolean(param),
            buffer: (param) => this.emitPush(param),
            object: (param) => this.emitPushObject(param),
        };
    }
    get buffers() {
        return this.mutableBuffers;
    }
    emitPush(value) {
        if (value.length <= Op.PUSHBYTES75) {
            this.emitOpByte(value.length, value);
        }
        else if (value.length < 0x100) {
            this.emitOp('PUSHDATA1');
            this.emitUInt8(value.length);
            this.emit(value);
        }
        else if (value.length < 0x10000) {
            this.emitOp('PUSHDATA2');
            this.emitUInt16LE(value.length);
            this.emit(value);
        }
        else if (value.length < 0x100000000) {
            this.emitOp('PUSHDATA4');
            this.emitUInt32LE(value.length);
            this.emit(value);
        }
        else {
            throw new Error('Invalid buffer length');
        }
        return this;
    }
    emitUInt8(value) {
        const buff = Buffer.allocUnsafe(1);
        buff.writeUInt8(value, 0);
        return this.emit(buff);
    }
    emitUInt16LE(value) {
        const buff = Buffer.allocUnsafe(2);
        buff.writeUInt16LE(value, 0);
        return this.emit(buff);
    }
    emitInt16LE(value) {
        const buff = Buffer.allocUnsafe(2);
        buff.writeInt16LE(value, 0);
        return this.emit(buff);
    }
    emitUInt32LE(value) {
        const buff = Buffer.allocUnsafe(4);
        buff.writeUInt32LE(value, 0);
        return this.emit(buff);
    }
    emitPushInt(valueIn) {
        const value = new BN(valueIn);
        if (value.eq(utils.NEGATIVE_ONE)) {
            return this.emitOp('PUSHM1');
        }
        if (value.eq(utils.ZERO)) {
            return this.emitOp('PUSH0');
        }
        if (value.gt(utils.ZERO) && value.lt(utils.SIXTEEN)) {
            return this.emitOpByte(Op.PUSH1 - 1 + value.toNumber());
        }
        return this.emitPush(utils.toSignedBuffer(value));
    }
    emitPushUInt160(value) {
        return this.emitPush(common.uInt160ToBuffer(value));
    }
    emitPushUInt256(value) {
        return this.emitPush(common.uInt256ToBuffer(value));
    }
    emitPushECPoint(ecPoint) {
        return this.emitPush(common.ecPointToBuffer(ecPoint));
    }
    emitPushString(value) {
        return this.emitPush(Buffer.from(value, 'utf8'));
    }
    emitPushBoolean(value) {
        return this.emitOp(value ? 'PUSH1' : 'PUSH0');
    }
    emitOp(op, buffer) {
        this.emitOpByte(Op[op], buffer);
        return this;
    }
    emitPushParam(param) {
        return scriptBuilderParamTo(param, this.pushParamCallbacks);
    }
    emitPushParams(...params) {
        for (let i = params.length - 1; i >= 0; i -= 1) {
            this.emitPushParam(params[i]);
        }
        return this;
    }
    emitPushArray(params) {
        this.emitPushParams(...params);
        this.emitPushParam(params.length);
        return this.emitOp('PACK');
    }
    emitPushMap(params) {
        this.emitOp('NEWMAP');
        params.forEach((value, key) => {
            this.emitOp('DUP');
            this.emitPushParam(key);
            this.emitPushParam(value);
            this.emitOp('SETITEM');
        });
        return this;
    }
    emitPushObject(params) {
        this.emitOp('NEWMAP');
        Object.entries(params).forEach(([key, value]) => {
            this.emitOp('DUP');
            this.emitPushParam(key);
            this.emitPushParam(value);
            this.emitOp('SETITEM');
        });
        return this;
    }
    emitAppCallInvocation(operation, ...params) {
        this.emitPushArray(params);
        return this.emitPushParam(operation);
    }
    emitAppCallVerification(scriptHash) {
        return this.emitOp('APPCALL', common.uInt160ToBuffer(scriptHash));
    }
    emitAppCall(scriptHash, operation, ...params) {
        this.emitAppCallInvocation(operation, ...params);
        return this.emitAppCallVerification(scriptHash);
    }
    emitTailCall(scriptHash, operation, ...params) {
        this.emitAppCallInvocation(operation, ...params);
        return this.emitOp('TAILCALL', common.uInt160ToBuffer(scriptHash));
    }
    emitSysCall(sysCall, ...params) {
        this.emitPushParams(...params);
        const sysCallBuffer = Buffer.from(sysCall, 'ascii');
        const writer = new BinaryWriter();
        writer.writeVarBytesLE(sysCallBuffer);
        return this.emitOp('SYSCALL', writer.toBuffer());
    }
    emitOpByte(byteCode, buffer) {
        const byteCodeBuffer = ByteBuffer[byteCode];
        this.emit(byteCodeBuffer);
        this.emit(buffer);
        return this;
    }
    emit(buffer) {
        if (buffer !== undefined) {
            this.mutableBuffers.push(buffer);
        }
        return this;
    }
    build() {
        return Buffer.concat(this.mutableBuffers);
    }
}

//# sourceMappingURL=ScriptBuilder.js.map
